// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A drawer component for collecting serials for serialized parts.
 * Allows users to add, search, filter, and remove serials with
 * duplicate validation and mismatch warnings.
 */
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import {
  AddOutlined,
  ArrowBackOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  UploadFileOutlined,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { parseSerialsFile } from '@/shipment/utilities/parse-serials-file';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

type FilterType = 'all' | 'valid' | 'invalid';

interface Props {
  /**
   * Whether the drawer is open.
   */
  isOpen: boolean;
  /**
   * Function to call when the drawer should be closed.
   */
  onClose: () => void;
  /**
   * The SKU of the part - used as unique identifier.
   */
  partSku: string;
  /**
   * The name of the part.
   */
  partName: string;
  /**
   * The quantity received for this part.
   */
  receivedQuantity: number;
  /**
   * The current list of serials.
   */
  serials: string[];
  /**
   * Callback fired when the serials list changes.
   */
  onSerialsChange: (serials: string[]) => void;
  /**
   * Unique identifier for the item (used to track which item's serials are being edited).
   */
  itemId: string;
  /**
   * Set of serials that have been validated as invalid by the backend.
   */
  invalidSerials?: Set<string>;
  /**
   * Set of all serials used across all products in the shipment (excluding current item's serials).
   * Used to prevent duplicate serials across different products.
   */
  globalSerials?: Set<string>;
  /**
   * When true, the drawer is read-only and doesn't allow adding or removing serials.
   */
  readOnly?: boolean;
}

/**
 * A drawer for collecting serials for serialized parts.
 * Features:
 * - Add serials with duplicate validation
 * - Search and filter serials
 * - Remove individual serials
 * - Warning when serial count doesn't match received quantity
 * - Real-time form state updates
 */
export function SerialCollectionDrawer(props: Props) {
  // Destructure props for stable references in callbacks
  const {
    onSerialsChange,
    receivedQuantity,
    isOpen,
    itemId,
    serials,
    invalidSerials,
    globalSerials,
    readOnly = false,
  } = props;

  const { showToast } = useToast();

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [inputError, setInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserver = useResizeObserver(listContainerRef);
  const theme = useTheme();

  // Track which item we're currently editing
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);

  // Local state for serials - initialize from props to avoid flash
  const [localSerials, setLocalSerials] = useState<string[]>(serials);

  // Case-insensitive lookup set for O(1) duplicate checking
  const serialLookupRef = useState(() => new Set<string>())[0];

  // Duplicate serials tracking (local duplicates only)
  const [duplicateSerials, setDuplicateSerials] = useState<Set<string>>(
    new Set()
  );

  // Combined invalid serials (duplicates + backend-validated invalid)
  const allInvalidSerials = useMemo(() => {
    const combined = new Set(duplicateSerials);
    if (invalidSerials) {
      invalidSerials.forEach((serial) => combined.add(serial));
    }
    return combined;
  }, [duplicateSerials, invalidSerials]);

  // Initialize/reset serials state only when drawer opens or when switching items
  useEffect(() => {
    if (isOpen) {
      // Initialize if first open OR if we're editing a different item
      if (currentItemId !== itemId) {
        setLocalSerials(serials);
        serialLookupRef.clear();
        serials.forEach((serial) => {
          serialLookupRef.add(serial.toLowerCase());
        });
        setDuplicateSerials(new Set());
        setInputError(null);
        setInputValue('');
        setSearchQuery('');
        setFilter('all');
        setCurrentItemId(itemId);
      }
    } else {
      // Reset when drawer closes
      setCurrentItemId(null);
    }
  }, [isOpen, itemId, serials, currentItemId, serialLookupRef]);

  const handleAddSerial = useCallback(() => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      return;
    }

    // Check for duplicates using O(1) lookup
    const lowerCaseValue = trimmedValue.toLowerCase();
    if (serialLookupRef.has(lowerCaseValue)) {
      setInputError('Serials already exists');
      return;
    }

    // Check against global serials (serials from other products)
    if (globalSerials?.has(trimmedValue)) {
      setInputError('Serials is already used in another product');
      return;
    }

    // Clear error and add serial to local state and notify parent
    setInputError(null);
    const updatedSerials = [...localSerials, trimmedValue];
    setLocalSerials(updatedSerials);
    serialLookupRef.add(lowerCaseValue);
    onSerialsChange(updatedSerials);
    setInputValue('');

    // Auto-focus the input after adding, unless we've reached or exceeded the received quantity
    const shouldRefocus = updatedSerials.length < receivedQuantity;

    if (shouldRefocus) {
      // Use setTimeout to ensure focus happens after React updates
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } else {
      // If we've reached the target, blur the input
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }, 0);
    }
  }, [
    inputValue,
    localSerials,
    serialLookupRef,
    onSerialsChange,
    receivedQuantity,
    globalSerials,
  ]);

  const isButtonDisabled = useMemo(
    () => readOnly || inputValue.trim() === '',
    [readOnly, inputValue]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setInputError(null);
  }, []);

  const handleRemoveSerial = useCallback(
    (serialToRemove: string) => {
      const newSerials = localSerials.filter(
        (serial) => serial !== serialToRemove
      );
      setLocalSerials(newSerials);
      serialLookupRef.delete(serialToRemove.toLowerCase());
      onSerialsChange(newSerials);

      // Remove from duplicates if it was marked as duplicate
      setDuplicateSerials((prev) => {
        if (prev.has(serialToRemove)) {
          const newDuplicates = new Set(prev);
          newDuplicates.delete(serialToRemove);
          return newDuplicates;
        }
        return prev;
      });
    },
    [localSerials, serialLookupRef, onSerialsChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAddSerial();
      }
    },
    [handleAddSerial]
  );

  /**
   * Handles file upload button click by triggering the hidden file input.
   */
  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Handles file selection and parses the file to populate serials.
   * Deduplicates against existing serials and shows appropriate toasts.
   */
  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;

        try {
          const { serials: parsedSerials, duplicatesInFile } = parseSerialsFile(
            text,
            file.name
          );

          if (parsedSerials.length === 0) {
            showToast({
              element: <>File is empty or has no valid serials</>,
              severity: 'warning',
            });
            return;
          }

          // Filter out serials that already exist (case-insensitive)
          const newSerials: string[] = [];
          let existingDuplicates = 0;
          let globalDuplicates = 0;

          for (const serial of parsedSerials) {
            const lowerSerial = serial.toLowerCase();
            if (serialLookupRef.has(lowerSerial)) {
              existingDuplicates++;
            } else if (globalSerials?.has(serial)) {
              globalDuplicates++;
            } else {
              newSerials.push(serial);
            }
          }

          if (newSerials.length === 0) {
            showToast({
              element: <>All serials in file are duplicates</>,
              severity: 'warning',
            });
            return;
          }

          // Add new serials to local state
          const updatedSerials = [...localSerials, ...newSerials];
          setLocalSerials(updatedSerials);
          newSerials.forEach((serial) => {
            serialLookupRef.add(serial.toLowerCase());
          });
          onSerialsChange(updatedSerials);

          // Build toast message
          const totalDuplicates =
            duplicatesInFile + existingDuplicates + globalDuplicates;
          if (totalDuplicates > 0) {
            showToast({
              element: (
                <>
                  Added {pluralize(newSerials.length, { singular: 'serial' })}.{' '}
                  {pluralize(totalDuplicates, { singular: 'duplicate' })}{' '}
                  ignored.
                </>
              ),
              severity: 'warning',
            });
          } else {
            showToast({
              element: (
                <>
                  Added {pluralize(newSerials.length, { singular: 'serial' })}{' '}
                  from file
                </>
              ),
              severity: 'success',
            });
          }
        } catch (error) {
          showToast({
            element: (
              <>
                {error instanceof Error
                  ? error.message
                  : 'Failed to parse file'}
              </>
            ),
            severity: 'error',
          });
        }
      };

      reader.readAsText(file);

      // Reset the file input so the same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [localSerials, serialLookupRef, onSerialsChange, showToast, globalSerials]
  );

  // Filter serials based on search query and filter type
  const filteredSerials = useMemo(
    () =>
      localSerials.filter((serial) => {
        const matchesSearch = serial
          .toLowerCase()
          .includes(searchQuery.toLowerCase());

        if (filter === 'valid') {
          return matchesSearch && !allInvalidSerials.has(serial);
        }
        if (filter === 'invalid') {
          return matchesSearch && allInvalidSerials.has(serial);
        }
        return matchesSearch;
      }),
    [localSerials, searchQuery, filter, allInvalidSerials]
  );

  const validCount = useMemo(
    () => localSerials.filter((s) => !allInvalidSerials.has(s)).length,
    [localSerials, allInvalidSerials]
  );

  const invalidCount = allInvalidSerials.size;

  return (
    <Paper
      elevation={3}
      square
      sx={{
        width: 500,
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      {/* Hidden file input for serial upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.txt"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <Stack gap={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Serials for {props.partSku}</Typography>
          <IconButton onClick={props.onClose} size="small">
            <ArrowBackOutlined />
          </IconButton>
        </Stack>

        {/* Summary */}
        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              {props.partName} received:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(receivedQuantity)}
            </Typography>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Serials entered:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatNumber(localSerials.length)}
            </Typography>
          </Stack>
          {!readOnly && (
            <Box sx={{ mt: 1, textAlign: 'right' }}>
              {(() => {
                const serialCount = localSerials.length;
                const expectedQuantity = receivedQuantity;

                if (invalidCount > 0) {
                  return (
                    <Typography variant="body2" sx={{ color: 'error.main' }}>
                      (
                      {pluralize(serialCount, {
                        singular: 'serial',
                        plural: 'serials',
                        includeCount: true,
                      })}{' '}
                      entered (
                      {pluralize(invalidCount, {
                        singular: 'invalid',
                        plural: 'invalid',
                        includeCount: true,
                      })}
                      ))
                    </Typography>
                  );
                } else if (serialCount === 0 && expectedQuantity === 0) {
                  return (
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      (Complete)
                    </Typography>
                  );
                } else if (serialCount === 0) {
                  return (
                    <Typography variant="body2" sx={{ color: 'error.main' }}>
                      (
                      {pluralize(expectedQuantity, {
                        singular: 'serial',
                        plural: 'serials',
                        includeCount: true,
                      })}{' '}
                      needed)
                    </Typography>
                  );
                } else if (serialCount === expectedQuantity) {
                  return (
                    <Typography variant="body2" sx={{ color: 'success.main' }}>
                      (Complete)
                    </Typography>
                  );
                } else if (serialCount > expectedQuantity) {
                  const extra = serialCount - expectedQuantity;
                  return (
                    <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                      (
                      {pluralize(extra, {
                        singular: 'extra serial',
                        plural: 'extra serials',
                        includeCount: true,
                      })}
                      )
                    </Typography>
                  );
                }
                const needed = expectedQuantity - serialCount;
                return (
                  <Typography variant="body2" sx={{ color: 'error.main' }}>
                    (
                    {pluralize(needed, {
                      singular: 'serial',
                      plural: 'serials',
                      includeCount: true,
                    })}{' '}
                    needed)
                  </Typography>
                );
              })()}
            </Box>
          )}
        </Box>

        {/* Add serial input - hidden in read-only mode */}
        {!readOnly && (
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              fullWidth
              placeholder="Add serial"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              size="small"
              error={!!inputError}
              helperText={inputError}
              inputRef={inputRef}
            />
            <Tooltip
              title={
                isButtonDisabled
                  ? 'Enter a serial to add'
                  : 'Add individual serial'
              }
              arrow
            >
              <span>
                <IconButton
                  onClick={handleAddSerial}
                  disabled={isButtonDisabled}
                  sx={{
                    height: '100%',
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: theme.palette.secondary.dark,
                    },
                  }}
                >
                  <AddOutlined />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Upload a list of serials in CSV format" arrow>
              <IconButton
                onClick={handleUploadClick}
                sx={{
                  height: '100%',
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'secondary',
                  },
                }}
              >
                <UploadFileOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        )}

        {/* Filters and search */}
        <Stack direction="row" gap={2} alignItems="center">
          <Box sx={{ width: '66%' }}>
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={(_, newFilter) => {
                if (newFilter !== null) {
                  setFilter(newFilter);
                }
              }}
              size="small"
              fullWidth
            >
              <ToggleButton value="all">
                <Typography variant="button" textTransform="none">
                  All ({formatNumber(localSerials.length)})
                </Typography>
              </ToggleButton>
              <ToggleButton value="valid">
                <Typography variant="button" textTransform="none">
                  Valid ({formatNumber(validCount)})
                </Typography>
              </ToggleButton>
              <ToggleButton value="invalid">
                <Typography variant="button" textTransform="none">
                  Invalid ({formatNumber(invalidCount)})
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ width: '33%' }}>
            <TextField
              fullWidth
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Stack>

        {/* Serial list */}
        <Box ref={listContainerRef} sx={{ flex: 1, minHeight: 0 }}>
          {filteredSerials.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                minHeight: '20vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No serials added yet
              </Typography>
            </Paper>
          ) : (
            <FixedSizeList
              height={resizeObserver.height}
              width={resizeObserver.width}
              itemSize={36}
              itemCount={Math.ceil(filteredSerials.length / 3)}
              overscanCount={10}
            >
              {({ index, style }: ListChildComponentProps) => {
                const startIdx = index * 3;
                const rowSerials = filteredSerials.slice(
                  startIdx,
                  startIdx + 3
                );
                return (
                  <Box
                    style={style}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: 2,
                      pr: 1,
                    }}
                  >
                    {rowSerials.map((serial) => {
                      const isValid = !allInvalidSerials.has(serial);
                      return (
                        <Stack
                          key={serial}
                          direction="row"
                          alignItems="center"
                          gap={1}
                          sx={{
                            borderLeft: 3,
                            borderRadius: '5px',
                            borderColor: isValid
                              ? 'success.main'
                              : 'error.main',
                            pl: 1,
                            py: 0.5,
                            minWidth: 0,
                          }}
                        >
                          {isValid && (
                            <CheckOutlined
                              fontSize="small"
                              sx={{ color: 'success.main', flexShrink: 0 }}
                            />
                          )}
                          <ConditionalTooltip
                            tooltipText={
                              serial.length > 11 ? serial : undefined
                            }
                            arrow
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {serial}
                            </Typography>
                          </ConditionalTooltip>
                          {!readOnly && (
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSerial(serial)}
                              sx={{ p: 0.5, flexShrink: 0 }}
                            >
                              <CloseOutlined fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      );
                    })}
                  </Box>
                );
              }}
            </FixedSizeList>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
